import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import ItemPanel from '../ui/ItemPanel';
import { muteConversation } from '../../services/conversation';

const HistoryItem = ({ id, title, onClick, isActive, onDelete, onLabelChange }) => {
	const [isPanelOpen, setIsPanelOpen] = useState(false);
	const [label, setLabel] = useState(title);
	const [isEditingLabel, setIsEditingLabel] = useState(false);
	const panelRef = useRef(null);
	const inputRef = useRef(null);
	const isSavingRef = useRef(false);
	const menuItems = [
		// { label: 'history.share' },
		{
			label: 'history.label_action',
			icon: 'ri-edit-line',
			onClick: async (e) => {
				e.stopPropagation();
				setIsEditingLabel(true);
				togglePanel(e);
			},
		},
		{
			label: 'history.print',
			icon: 'ri-printer-line',
			onClick: async (e) => {
				e.stopPropagation();
				window.open(`/query/${id}/print`, '_blank', 'noopener,noreferrer');
			},
		},
		{
			label: 'history.delete',
			icon: 'ri-delete-bin-line',
			onClick: async (e) => {
				e.stopPropagation();
				const response = await muteConversation(id);
				if (response) {
					onDelete();
				}
				togglePanel(e);
			},
		},
	];

	const togglePanel = (e) => {
		e.stopPropagation();
		setIsPanelOpen((prev) => !prev);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (panelRef.current && !panelRef.current.contains(event.target)) {
				setIsPanelOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const capitalizeQuestion = (str) => {
		if (!str) return '';
		return str.charAt(0).toUpperCase() + str.slice(1);
	};

	const handleSave = () => {
		isSavingRef.current = true;
		if (onLabelChange) {
			onLabelChange(label);
		}
		setIsEditingLabel(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSave();
		}
	};

	useEffect(() => {
		if (isEditingLabel && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditingLabel]);

	const handleBlur = () => {
		if (!isSavingRef.current) {
			setLabel(title); // Reset to original title if not saved
		}
		isSavingRef.current = false;
		setIsEditingLabel(false);
	};

	return (
		<div
			onClick={onClick}
			className={`group cursor-pointer flex justify-between items-center px-1 ${
				isActive ? 'bg-gray-100 rounded-sm' : 'hover:bg-gray-100 rounded-sm'
			}`}
		>
			{!isEditingLabel && (
				<>
					<p title={capitalizeQuestion(title)} className='py-2 text-primary-text text-base truncate max-w-45'>
						{capitalizeQuestion(title)}
					</p>

					<div className='relative' ref={panelRef}>
						<div className='absolute right-0 top-0 mt-[-14px]'>
							<button
								onClick={togglePanel}
								className='cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity'
							>
								<i className='ri-more-line text-primary-text text-lg'></i>
							</button>
						</div>
						<div className='relative'>
							{isPanelOpen && (
								<ItemPanel
									items={menuItems}
									positionClass='left-[-100px] top-full mt-4 min-w-28'
									zIndex='z-20'
								/>
							)}
						</div>
					</div>
				</>
			)}
			{isEditingLabel && (
				<div className='relative w-full'>
					<input
						ref={inputRef}
						type='text'
						value={label}
						autoFocus
						onChange={(e) => setLabel(e.target.value)}
						onKeyDown={handleKeyDown}
						onBlur={handleBlur}
						className='py-2 text-primary-text text-base w-full pr-8 focus-visible:outline-none'
					/>
					<button
						type='button'
						onClick={handleSave}
						onMouseDown={(e) => e.preventDefault()}
						className='absolute right-2 top-1/2 transform -translate-y-1/2 text-primary-text text-base cursor-pointer hover:opacity-70'
					>
						⏎
					</button>
				</div>
			)}
		</div>
	);
};

HistoryItem.propTypes = {
	id: PropTypes.number.isRequired,
	title: PropTypes.string.isRequired,
	onClick: PropTypes.func,
	isActive: PropTypes.bool,
};

export default HistoryItem;
